/**
 * "Which project, and which phase of it" - the pair, as one control.
 *
 * Lifted out of `NewTaskModal` when a second dialog needed the same two fields
 * (filing a support ticket as a task). The pair is not two fields that happen to
 * sit together: the phase list has to be fetched when the project changes, and
 * the phase id has to be dropped at the same moment, because a milestone belongs
 * to exactly one project and keeping the id across a project change is how a
 * task ends up filed under another project's phase - a row the server refuses
 * and, before it did, a row that was counted in one project and listed in
 * another.
 *
 * That rule is the reason this is a component rather than a copied `useEffect`.
 * Two copies of "clear the phase when the project changes" is one copy away from
 * a dialog that forgets, and the bug it produces is invisible on the screen that
 * causes it.
 *
 * The phase field is drawn only when the chosen project HAS phases. A control
 * whose single option is "no milestone" is a control that cannot do anything,
 * and on a task with no project there is not even a project to file it under.
 *
 * Both are the app's own dropdowns rather than the browser's. A native `<select>`
 * inside a `.crm-inp` pill draws no arrow at all and then opens a grey operating
 * system list over the dialog, so the two fields read as flat text until they are
 * clicked. They differ in which one: the project list is as long as the account
 * is old, so it gets `PickerField` and its search box; a project's phases are
 * counted on one hand, so `MenuField` lists them all at once.
 *
 * Needs a `PopHost` above it - both controls are `Pop` menus, and a `Pop` with no
 * host is a field that silently never opens. Every dialog that mounts this pair
 * carries one.
 */
import { useEffect, useMemo, useState } from "react";
import { useT } from "../../../i18n/I18nProvider";
import { getProject, type Milestone, type Project } from "../../../lib/projects";
import { MenuField, PickerField } from "../../ui";

export function ProjectPhaseFields({
  projects,
  projectId,
  milestoneId,
  onProjectChange,
  onMilestoneChange,
}: {
  /** What the project select offers. The caller owns the fetch and the order. */
  projects: Project[];
  projectId: string;
  milestoneId: string;
  onProjectChange: (id: string) => void;
  /** Called with "" whenever the project changes, so the caller cannot keep a
   *  phase that belongs somewhere else. */
  onMilestoneChange: (id: string) => void;
}) {
  const t = useT();
  /** The chosen project's phases: null while unknown, a list once answered. */
  const [phases, setPhases] = useState<Milestone[] | null>(null);

  // The phases are fetched when the project is chosen rather than read off the
  // row: the list endpoint behind `projects` selects the client and a task count
  // and no milestones at all, so `p.milestones` is undefined for every row a
  // caller hands in. It is fetched even where the caller does hold them - a phase
  // added in the Milestones tab a minute ago has to be pickable here, and a stale
  // array would hide it.
  useEffect(() => {
    onMilestoneChange("");
    setPhases(null);
    if (!projectId) return;
    let alive = true;
    getProject(projectId)
      .then((p) => {
        if (alive) setPhases(p.milestones);
      })
      .catch(() => {
        // An empty list hides the field. A project whose phases cannot be read
        // must still accept a task; losing the option is far better than losing
        // the task.
        if (alive) setPhases([]);
      });
    return () => {
      alive = false;
    };
    // `onMilestoneChange` is deliberately out of the deps: it is a setter the
    // caller re-creates on every render, and listing it would re-run the fetch
    // (and clear the phase) on every keystroke in the dialog around this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  /** "No project" is a listed row rather than a blank first line: filing nothing
   *  under a project is a decision somebody takes, and an empty field does not
   *  look like one. */
  const projectOptions = useMemo(
    () => [
      { value: "", label: t("tasks.noProject") },
      ...projects.map((p) => ({ value: p.id, label: p.name })),
    ],
    [projects, t],
  );

  return (
    <>
      <div>
        <span className="crm-lab">{t("tasks.project")}</span>
        <PickerField
          variant="form"
          value={projectId}
          onChange={onProjectChange}
          options={projectOptions}
          label={t("tasks.project")}
          searchPlaceholder={t("tasks.project")}
          // Not "nothing matched": a search that finds no project in a company
          // that has projects is a typo, and the menu says so in those words.
          emptyLabel={t("palette.empty")}
        />
      </div>

      {/* Directly under the project, because it is a refinement of that answer
          and reads as nonsense on its own. */}
      {phases && phases.length > 0 ? (
        <div>
          <span className="crm-lab">{t("tasks.milestone")}</span>
          <MenuField
            variant="form"
            value={milestoneId}
            onChange={onMilestoneChange}
            label={t("tasks.milestone")}
            options={[
              { value: "", label: t("projects.noMilestone") },
              ...phases.map((m) => ({ value: m.id, label: m.name })),
            ]}
          />
        </div>
      ) : null}
    </>
  );
}
